import { Product, ProductCategoryCode, ProductType, Quote } from 'pleinchamp-api-client';
import type { ReactNode } from 'react';
import { ComponentProps } from 'react';
import {
  StocksAccordionElementWidth,
  StocksAccordionRowElement,
} from '@stocks/components/StocksTable/StocksAccordionRowElement.component';
import { StocksTableCell } from '@stocks/components/StocksTable/StocksTableCell.component';
import { StockVariation } from '@stocks/components/StockVariation/StockVariation.component';
import { Spacing } from '@styles/Spacing.style';
import { formatDateCompact, formatDateToTime, formatShortMonthWithYear } from '@utils/date';
import { formatNumberWithComma } from '@utils/number';
import { getStockDetailRouteParams } from './Stocks.utils';

interface ExtendedQuotes extends Omit<Quote, '_var'> {
  var: string;
}

export enum StocksTableElement {
  date,
  lastQuoteDate,
  refMarket,
  market,
  tax,
  averagePrice,
  variation1d,
  variation12m,
  conformation,
  minPrice,
  maxPrice,
  name,
  breed,
  sex,
  weight,
  maturity,
  hour,
  lastQuote,
  dailyVolume,
  opening,
  highest,
  lowest,
  settlement,
  termVariation,
}
type StockScreen = 'stockHome' | 'stockDetail';

export type StocksTableInformationArgs = {
  productType: ProductType;
  product?: Product;
  rowForm: 'accordion' | 'table';
  tableHeader?: boolean;
  screen: StockScreen;
  onShowGraph?: (product: Product) => void;
  forceRef?: boolean;
};

export type ElementComponents = {
  mobile: ReactNode;
  desktopBody: ReactNode;
  desktopHeader: ReactNode;
  raw?: string | number;
};

export const StocksTableWidth = {
  // size of the star + left padding + right padding
  chartCell: Spacing.m + Spacing.s + Spacing.m,
  // size of the text + left padding + right padding
  favoriteCell: Spacing.m + Spacing.m + Spacing.s,
  // size of the star + left padding + right padding
  moreQuote: 110 + Spacing.s + Spacing.m,
  refMarket: 300,
};

function buildElementComponents(args: {
  element?: ElementComponents['raw'];
  i18nTitle: string;
  mobileWidth: StocksAccordionElementWidth;
  unit?: string;
  tableRowHeader?: ComponentProps<typeof StocksTableCell>['header'];
  isVariation?: boolean;
  desktopWidth?: number;
}): ElementComponents {
  const { element, i18nTitle, mobileWidth, unit, tableRowHeader, isVariation, desktopWidth } = args;
  return {
    desktopBody: (
      <StocksTableCell
        element={
          isVariation ? (
            <StockVariation amount={element as number} unit={unit} variant="table" />
          ) : typeof element === 'number' ? (
            formatNumberWithComma(element, 2)
          ) : (
            element
          )
        }
        header={tableRowHeader}
        key={i18nTitle}
        style={desktopWidth ? { width: desktopWidth } : undefined}
        unit={isVariation ? undefined : unit}
      />
    ),
    desktopHeader: (
      <StocksTableCell
        element=""
        header="headerInHeader"
        key={i18nTitle}
        style={desktopWidth ? { width: desktopWidth } : undefined}
        textProps={{ i18nKey: i18nTitle }}
      />
    ),
    mobile: (
      <StocksAccordionRowElement
        element={
          isVariation ? (
            <StockVariation amount={element as number} unit={unit} variant="accordion" />
          ) : typeof element === 'number' ? (
            formatNumberWithComma(element, 2)
          ) : (
            element
          )
        }
        i18nTitle={i18nTitle}
        key={i18nTitle}
        type={mobileWidth}
        unit={isVariation ? undefined : unit}
      />
    ),
    raw: element,
  };
}
function getStocksTableElement(args: {
  productType: ProductType;
  product: Product;
  type: StocksTableElement;
  mobileWidth: StocksAccordionElementWidth;
  desktopWidth?: number;
  tableRowHeader?: ComponentProps<typeof StocksTableCell>['header'];
}): ElementComponents {
  const { productType, product, type, mobileWidth, tableRowHeader, desktopWidth } = args;
  const { marketReference, name } = productType;
  const { unit, quote, cattleDetail, market, deliveryDate } = product || {};
  // This tricks below is required to prevent type error from reserved keyword `var` instead of change API.
  const { var: varQuote } = (quote as ExtendedQuotes) || {};

  const { date, tax, price, variation1d, variation12m, minPrice, maxPrice, totalVolume, open, high, low, settlement } =
    quote || {};

  function getDetails() {
    switch (type) {
      case StocksTableElement.date:
      case StocksTableElement.lastQuoteDate: {
        return {
          element: formatDateCompact(date),
          i18nTitle: type === StocksTableElement.lastQuoteDate ? 'stocks.last-quote' : 'stocks.date',
        };
      }
      case StocksTableElement.refMarket: {
        return {
          element: marketReference?.marketName,
          i18nTitle: 'stocks.reference-market',
        };
      }
      case StocksTableElement.market: {
        return {
          element: market?.marketName,
          i18nTitle: 'stocks.market',
        };
      }
      case StocksTableElement.tax: {
        return {
          element: tax,
          i18nTitle: 'stocks.tax',
          withUnit: true,
        };
      }
      case StocksTableElement.averagePrice:
      case StocksTableElement.lastQuote: {
        return {
          element: price,
          i18nTitle:
            type === StocksTableElement.averagePrice ? 'stocks.average-price' : 'stocks.term.table.header.last-quote',
          withUnit: true,
        };
      }
      case StocksTableElement.variation1d: {
        return {
          element: variation1d,
          i18nTitle: 'stocks.variation',
          isVariation: true,
          withUnit: true,
        };
      }
      case StocksTableElement.variation12m: {
        return {
          element: variation12m,
          i18nTitle: 'stocks.long-term-variation',
          isVariation: true,
          withUnit: true,
        };
      }
      case StocksTableElement.termVariation: {
        return {
          element: varQuote,
          i18nTitle: 'stocks.variation',
          isVariation: true,
          withUnit: true,
        };
      }
      case StocksTableElement.conformation: {
        return {
          element: cattleDetail?.conformation,
          i18nTitle: 'stocks.conformation',
        };
      }
      case StocksTableElement.minPrice: {
        return {
          element: minPrice,
          i18nTitle: 'stocks.min-price',
          withUnit: true,
        };
      }
      case StocksTableElement.maxPrice: {
        return {
          element: maxPrice,
          i18nTitle: 'stocks.max-price',
          withUnit: true,
        };
      }
      case StocksTableElement.name: {
        return {
          element: name,
          i18nTitle: 'stocks.quote',
        };
      }
      case StocksTableElement.breed: {
        return {
          element: cattleDetail?.breed,
          i18nTitle: 'stocks.breed',
        };
      }
      case StocksTableElement.sex: {
        return {
          element: cattleDetail?.sex,
          i18nTitle: 'stocks.sex',
        };
      }
      case StocksTableElement.weight: {
        return {
          element: cattleDetail?.weight,
          i18nTitle: 'stocks.weight',
        };
      }
      case StocksTableElement.maturity: {
        return {
          element: deliveryDate ? formatShortMonthWithYear(deliveryDate) : undefined,
          i18nTitle: 'stocks.term.table.header.maturity',
        };
      }
      case StocksTableElement.hour: {
        return {
          element: formatDateToTime(date),
          i18nTitle: 'stocks.term.table.header.hour',
        };
      }
      case StocksTableElement.dailyVolume: {
        return {
          element: totalVolume,
          i18nTitle: 'stocks.term.table.header.daily-volume',
        };
      }
      case StocksTableElement.opening: {
        return {
          element: open,
          i18nTitle: 'stocks.term.table.header.opening',
          withUnit: true,
        };
      }
      case StocksTableElement.highest: {
        return {
          element: high,
          i18nTitle: 'stocks.term.table.header.highest',
          withUnit: true,
        };
      }
      case StocksTableElement.lowest: {
        return {
          element: low,
          i18nTitle: 'stocks.term.table.header.lowest',
          withUnit: true,
        };
      }
      case StocksTableElement.settlement: {
        return {
          element: settlement,
          i18nTitle: 'stocks.term.table.header.settlement',
          withUnit: true,
        };
      }

      default: {
        const switchTest: never = type;
        return switchTest;
      }
    }
  }
  const { element, i18nTitle, withUnit, isVariation } = getDetails();

  return buildElementComponents({
    desktopWidth,
    element,
    i18nTitle,
    isVariation,
    mobileWidth,
    tableRowHeader,

    unit: withUnit ? (productType.productCategoryCode === ProductCategoryCode.Cattle ? '€' : unit) : undefined,
  });
}

export function getStocksTableElements(
  args: Omit<StocksTableInformationArgs, 'rowForm' | 'onShowgraph' | 'tableHeader'> & { product: Product }
) {
  const { productType, product, screen, forceRef } = args;
  const { productCategoryCode, marketReference } = productType;

  switch (productCategoryCode) {
    case ProductCategoryCode.Crops: {
      if (screen === 'stockHome') {
        return {
          elements: [],
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.name,
          }),
          linkProps: getStockDetailRouteParams(productType, marketReference),
        };
      }
      if (screen === 'stockDetail') {
        return {
          elements: [
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.lastQuoteDate },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.tax },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.averagePrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.variation1d },
          ].map((e) => getStocksTableElement({ ...e, product, productType })),
          firstElement: getStocksTableElement({
            desktopWidth: StocksTableWidth.refMarket,
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: forceRef ? StocksTableElement.refMarket : StocksTableElement.market,
          }),
        };
      }
      const ifTest: never = screen;
      return ifTest;
    }
    case ProductCategoryCode.CattleFood: {
      if (screen === 'stockHome') {
        return {
          elements: [],
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.name,
          }),
          linkProps: getStockDetailRouteParams(productType),
        };
      }
      if (screen === 'stockDetail') {
        return {
          elements: [
            { mobileWidth: StocksAccordionElementWidth.full, type: StocksTableElement.lastQuoteDate },
            { mobileWidth: StocksAccordionElementWidth.full, type: StocksTableElement.tax },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.averagePrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.variation1d },
          ].map((e) => getStocksTableElement({ ...e, product, productType })),
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.market,
          }),
        };
      }
      const ifTest: never = screen;
      return ifTest;
    }
    case ProductCategoryCode.Energies: {
      if (screen === 'stockHome') {
        return {
          elements: [],
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.name,
          }),
          linkProps: getStockDetailRouteParams(productType),
        };
      }
      if (screen === 'stockDetail') {
        return {
          elements: [
            { mobileWidth: StocksAccordionElementWidth.full, type: StocksTableElement.lastQuoteDate },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.averagePrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.variation1d },
          ].map((e) => getStocksTableElement({ ...e, product, productType })),
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.market,
          }),
        };
      }
      const ifTest: never = screen;
      return ifTest;
    }
    case ProductCategoryCode.Pork: {
      if (screen === 'stockHome') {
        return {
          elements: [],
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.name,
          }),
          linkProps: getStockDetailRouteParams(productType),
        };
      }
      if (screen === 'stockDetail') {
        return {
          elements: [
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.variation12m },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.averagePrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.variation1d },
          ].map((e) => getStocksTableElement({ ...e, product, productType })),
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.lastQuoteDate,
          }),
        };
      }
      const ifTest: never = screen;
      return ifTest;
    }
    case ProductCategoryCode.Cattle: {
      if (screen === 'stockHome') {
        // No cattle table on stockHome screen
        return {
          elements: [],
          firstElement: {} as ElementComponents,
        };
      }
      if (screen === 'stockDetail') {
        return {
          elements: [
            { mobileWidth: StocksAccordionElementWidth.full, type: StocksTableElement.conformation },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.sex },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.weight },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.minPrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.maxPrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.averagePrice },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.variation1d },
          ].map((e) => getStocksTableElement({ ...e, product, productType })),
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.breed,
          }),
        };
      }
      const ifTest: never = screen;
      return ifTest;
    }
    case ProductCategoryCode.CommoditiesFutures: {
      if (screen === 'stockHome') {
        // No market term table on stockHome screen
        return {
          elements: [],
          firstElement: {} as ElementComponents,
        };
      }
      if (screen === 'stockDetail') {
        return {
          elements: [
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.hour },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.lastQuote },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.termVariation },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.dailyVolume },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.opening },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.highest },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.lowest },
            { mobileWidth: StocksAccordionElementWidth.half, type: StocksTableElement.settlement },
          ].map((e) => getStocksTableElement({ ...e, product, productType })),
          firstElement: getStocksTableElement({
            mobileWidth: StocksAccordionElementWidth.full,
            product,
            productType,
            tableRowHeader: 'headerInRow',
            type: StocksTableElement.maturity,
          }),
        };
      }
      const ifTest: never = screen;
      return ifTest;
    }
    default: {
      const switchTest: never = productCategoryCode;
      return switchTest;
    }
  }
}
